import PropTypes from 'prop-types';
import React from 'react';
import { useMediaQuery } from 'react-responsive';

import CartCount from 'components/CartCount';
import HamburgerIcon from 'components/HamburgerIcon';
import { AllbirdsLogo, SearchSkinnyIcon } from 'components/Icon';
import IconButton from 'components/IconButton';
import { LargeUp } from 'components/MediaQuery';
import SearchIcon from 'components/SearchIcon';
import SearchModal from 'components/SearchModal';
import { largeUp } from 'constants/mediaQueries';
import { SEARCH_ORIGINATIONS } from 'constants/search';
import { TYPES } from 'constants/topNavigation';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

const LOGO_WIDTH = 98;
const HEADER_HEIGHT = 60;
const EXPOSED_SEARCH_ICON_SIZE = 17;

export { HEADER_HEIGHT };

const TopNavigationHeaderMobile = ({
  isTopNavigationOpen,
  toggleTopNavigation,
  itemCount,
  openCart,
  isCartOpen,
  trackNavigation,
  trackNavigationClick,
  trackSearchNavigation,
  showSearch,
  openQuickSearch,
  closeQuickSearch,
  isQuickSearchOpen,
}) => {
  const isHomepage = !!document.getElementById('page-home');
  const isSearchPage = !!document.getElementById('search-container');
  const isLargeUp = useMediaQuery({ query: largeUp });

  const showIconWithQuickSearch = showSearch && !isHomepage && !isSearchPage;
  const showExposedSearch = showSearch && isHomepage && !isTopNavigationOpen;
  const origin = showExposedSearch
    ? SEARCH_ORIGINATIONS.QUICK_SEARCH_BAR
    : SEARCH_ORIGINATIONS.QUICK_SEARCH_ICON;

  const handleLogoClick = () => {
    trackNavigation('/');
    trackNavigationClick({
      navigationType: TYPES.GLOBAL,
      navigationLevel1: I18n.localize('topNavigation.allbirdsHome'),
      navigationSource: window.location.href,
    });
  };

  return (
    <nav className="TopNavigationHeaderMobile">
      <div className="TopNavigationHeaderMobile__top">
        <div className="TopNavigationHeaderMobile__left">
          <IconButton
            onClick={toggleTopNavigation}
            aria-expanded={isTopNavigationOpen}
            aria-controls="top-navigation-drawer-mobile"
            aria-label={I18n.localize('topNavigation.toggleNavigation')}
          >
            <HamburgerIcon isOpen={isTopNavigationOpen} />
          </IconButton>
        </div>
        <a
          href="/"
          className="TopNavigationHeaderMobile__logo .TopNavigationHeaderMobile__center"
          aria-label={I18n.localize('topNavigation.allbirdsHome')}
          onClick={() => handleLogoClick()}
        >
          <AllbirdsLogo />
        </a>
        <div className="TopNavigationHeaderMobile__right">
          {showIconWithQuickSearch && !isLargeUp && (
            <IconButton
              className="TopNavigationHeaderMobile__cart-icon-button"
              onClick={() => openQuickSearch(SEARCH_ORIGINATIONS.QUICK_SEARCH_ICON)}
              aria-expanded={isCartOpen}
              aria-controls="cart"
              aria-label={I18n.localize('topNavigation.toggleCart')}
            >
              <SearchSkinnyIcon />
            </IconButton>
          )}

          {showSearch && (
            <LargeUp>
              <SearchIcon />
            </LargeUp>
          )}

          <IconButton
            className="TopNavigationHeaderMobile__cart-icon-button"
            onClick={openCart}
            aria-expanded={isCartOpen}
            aria-controls="cart"
            aria-label={I18n.localize('topNavigation.toggleCart')}
          >
            <CartCount itemCount={itemCount} />
          </IconButton>
        </div>
      </div>
      {showExposedSearch && (
        <button
          className="TopNavigationHeaderMobile__exposed-search"
          aria-label={I18n.localize('searchBar.enterSearchTerm')}
          id="TopNavigationHeaderMobile__exposed-search"
          onClick={() => openQuickSearch(SEARCH_ORIGINATIONS.QUICK_SEARCH_BAR)}
        >
          <span className="TopNavigationHeaderMobile__exposed-search-icon">
            <SearchSkinnyIcon width={EXPOSED_SEARCH_ICON_SIZE} height={EXPOSED_SEARCH_ICON_SIZE} />
          </span>
          <div className="TopNavigationHeaderMobile__exposed-search-text">
            {I18n.localize('searchBar.enterSearchTerm')}
          </div>
        </button>
      )}

      <SearchModal
        isOpen={isQuickSearchOpen}
        onClose={closeQuickSearch}
        origin={origin}
        trackSearchNavigation={trackSearchNavigation}
      />
      <style jsx>
        {`
          .TopNavigationHeaderMobile {
            position: relative;
            background-color: ${Theme.get('colors-white')};
            justify-content: space-between;
            align-items: center;
            width: 100%;
            padding: ${Theme.get('spacing') * 1.5}px ${Theme.get('spacing') * 2.5}px;
            box-shadow: 0 2px 24px 0 rgba(0, 0, 0, 0.09);
            z-index: 1; /* Added so dropshadow is above drawer below */
          }

          .TopNavigationHeaderMobile__top {
            display: flex;
          }

          .TopNavigationHeaderMobile__logo {
            width: ${LOGO_WIDTH}px;
            height: auto;
            margin: auto;
          }

          .TopNavigationHeaderMobile__right,
          .TopNavigationHeaderMobile__left {
            flex: 1;
          }

          .TopNavigationHeaderMobile__left {
            justify-content: flex-start;
          }

          .TopNavigationHeaderMobile__right {
            justify-content: flex-end;
            display: flex;
            align-items: center;
          }

          .TopNavigationHeaderMobile__exposed-search {
            margin-top: ${Theme.get('spacing') * 1.5}px;
            margin-bottom: 0;
            padding: ${Theme.get('spacing')}px;
            background-color: ${Theme.get('colors-grey')};
            display: flex;
            width: 100%;
            border: none;
            outline: none;
            border-radius: ${Theme.get('font-size-small')}px;
            max-height: 32px;
          }

          .TopNavigationHeaderMobile__exposed-search-icon,
          .TopNavigationHeaderMobile__exposed-search-text {
            margin: auto 0;
            padding-left: ${Theme.get('spacing')}px;
          }

          .TopNavigationHeaderMobile__exposed-search-text {
            padding-left: ${Theme.get('spacing') * 1.5}px;
            font-weight: ${Theme.get('font-weight-normal')};
            color: ${Theme.get('colors-black-80')};
          }

          @media ${largeUp} {
            .TopNavigationHeaderMobile__exposed-search {
              display: none;
            }
          }
        `}
      </style>
      <style jsx global>
        {`
          .TopNavigationHeaderMobile .IconButton {
            margin: 0;
          }

          body.Page:not(.password) {
            padding-top: ${showExposedSearch &&
            Theme.get('exposed-search-bar-height') + Theme.get('top-navigation-height')}px;
          }

          body.Page.Banner--enabled:not(.password) {
            padding-top: ${showExposedSearch &&
            Theme.get('exposed-search-bar-height') +
              Theme.get('top-navigation-height') +
              Theme.get('banner-height')}px;
          }

          @media ${largeUp} {
            body.Page:not(.password) {
              padding-top: ${showExposedSearch && Theme.get('top-navigation-height')}px;
            }

            body.Page.Banner--enabled:not(.password) {
              padding-top: ${showExposedSearch &&
              Theme.get('top-navigation-height') + Theme.get('banner-height')}px;
            }
          }
        `}
      </style>
    </nav>
  );
};

TopNavigationHeaderMobile.propTypes = {
  isTopNavigationOpen: PropTypes.bool.isRequired,
  toggleTopNavigation: PropTypes.func.isRequired,
  trackNavigation: PropTypes.func.isRequired,
  trackNavigationClick: PropTypes.func.isRequired,
  trackSearchNavigation: PropTypes.func.isRequired,
  itemCount: PropTypes.number,
  openCart: PropTypes.func.isRequired,
  isCartOpen: PropTypes.bool.isRequired,
  showSearch: PropTypes.bool.isRequired,
  openQuickSearch: PropTypes.func.isRequired,
  closeQuickSearch: PropTypes.func.isRequired,
  isQuickSearchOpen: PropTypes.bool.isRequired,
};

TopNavigationHeaderMobile.defaultProps = {
  itemCount: null,
};

export default TopNavigationHeaderMobile;
